"use client"

import * as React from "react"
import { Progress as ProgressPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * The value reaches the Radix root, which is what puts `aria-valuenow`, `aria-valuemin` and `aria-valuemax` on the
 * progressbar (it used to be dropped here, so every bar in the app was announced as an indeterminate one). A value
 * outside 0-100, or one that is not a number, is held to the range (Radix logs an error for those and draws nothing
 * useful); no value at all stays indeterminate and draws an empty bar. `indeterminate` asks it to move while there is
 * no number, for a job that is running with nothing to count yet: 0 is that too (a queued job, or a column's default,
 * has measured nothing, and a still empty bar reads as stuck). Any real progress wins.
 */
function Progress({
  className,
  value,
  indeterminate = false,
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root> & { indeterminate?: boolean }) {
  const held = typeof value === "number" && Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : undefined
  const sliding = indeterminate && !held
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      value={sliding ? undefined : held}
      className={cn(
        // Mirrored in right-to-left so the bar fills from the inline start.
        "relative h-2 w-full overflow-hidden rounded-full bg-primary/20 rtl-flip",
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={cn("h-full w-full flex-1 bg-primary", sliding ? "progress-sliding" : "transition-all")}
        style={sliding ? undefined : { transform: `translateX(-${100 - (held ?? 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  )
}

export { Progress }
